import React from 'react';
import { AlertTriangle, Lock, X, Users, Building2, Calendar, Activity, Trash2, ArrowLeft } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';

interface OrganizationDeleteDialogProps {
  orgId: string;
  orgName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const OrganizationDeleteDialog: React.FC<OrganizationDeleteDialogProps> = ({ orgId, orgName, onClose, onSuccess }) => {
  const queryClient = useQueryClient();

  const { data: constraints, isLoading: loadingConstraints } = useQuery({
    queryKey: ['org-deletion-check', orgId],
    queryFn: async () => {
      const [people, engagements, sessions] = await Promise.all([
        supabase.from('people').select('id', { count: 'exact', head: true }).eq('organization_id', orgId),
        supabase.from('engagement_organizations').select('id', { count: 'exact', head: true }).eq('organization_id', orgId),
        supabase.from('sessions').select('id', { count: 'exact', head: true }).eq('host_org_id', orgId)
      ]);

      return {
        people: people.count || 0,
        engagements: engagements.count || 0,
        sessions: sessions.count || 0,
        total: (people.count || 0) + (engagements.count || 0) + (sessions.count || 0)
      };
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('organizations').delete().eq('id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      if (onSuccess) onSuccess();
      onClose();
    }
  });

  const isBlocked = constraints && constraints.total > 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className={`bg-white w-full max-w-lg rounded-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] overflow-hidden border animate-in zoom-in-95 duration-200 ${isBlocked ? 'border-amber-200' : 'border-rose-200'}`}>
        
        {/* Header - Dynamic Theme */}
        <div className={`p-8 text-black flex items-center justify-between ${isBlocked ? 'bg-[#FFDB00]' : 'bg-rose-600 text-white'}`}>
           <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isBlocked ? 'bg-black/10 border-black/10' : 'bg-white/20 border-white/20'}`}>
                {isBlocked ? <Lock size={28} /> : <AlertTriangle size={28} />}
              </div>
              <div>
                 <h3 className="text-xl font-black uppercase tracking-tight">
                   {isBlocked ? 'Deletion Blocked' : 'Confirm Erasure'}
                 </h3>
                 <p className={`text-[10px] font-bold uppercase tracking-widest ${isBlocked ? 'text-black/60' : 'text-rose-100'}`}>
                   {isBlocked ? 'Active Dependency Conflict' : 'Permanent Destructive Action'}
                 </p>
              </div>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-black/10 rounded-lg transition-colors"><X size={24} /></button>
        </div>

        <div className="p-8 space-y-8">
          <div className="space-y-4">
             <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide leading-relaxed">
               Target Institution: <span className="text-slate-900 font-black">"{orgName}"</span>
             </p>
             
             <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-4">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-200 pb-2">
                  {isBlocked ? 'Registry Dependencies Identified:' : 'Safety Check Result:'}
                </p>
                
                {loadingConstraints ? (
                  <div className="py-6 flex items-center justify-center gap-3">
                    <Activity className={`animate-spin ${isBlocked ? 'text-amber-500' : 'text-rose-500'}`} size={18} />
                    <span className="text-[10px] font-black text-slate-300 uppercase animate-pulse">Scanning Data Integrity...</span>
                  </div>
                ) : isBlocked ? (
                  <div className="grid grid-cols-1 gap-3">
                     <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-3">
                           <Users size={14} className="text-amber-500" />
                           <span className="text-[10px] font-black text-slate-900 uppercase">Linked Personnel</span>
                        </div>
                        <span className="text-xs font-black text-slate-900">{constraints?.people}</span>
                     </div>
                     <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-3">
                           <Building2 size={14} className="text-amber-500" />
                           <span className="text-[10px] font-black text-slate-900 uppercase">Engagement Links</span>
                        </div>
                        <span className="text-xs font-black text-slate-900">{constraints?.engagements}</span>
                     </div>
                     <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-3">
                           <Calendar size={14} className="text-amber-500" />
                           <span className="text-[10px] font-black text-slate-900 uppercase">Session Hostings</span>
                        </div>
                        <span className="text-xs font-black text-slate-900">{constraints?.sessions}</span>
                     </div>
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <div className="inline-flex items-center gap-2 text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 text-[10px] font-black uppercase">
                      No dependencies found
                    </div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-3">This record is safe to remove from the registry.</p>
                  </div>
                )}
             </div>
          </div>

          <div className="space-y-3">
            {isBlocked ? (
              <>
                <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg flex items-start gap-3">
                  <Lock size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[10px] font-bold text-amber-700 uppercase leading-relaxed tracking-tight">
                    This institution cannot be purged while it is serving as a host or parent to active personnel. Reassign or delete linked records first.
                  </p>
                </div>
                <Button variant="primary" className="w-full py-5 text-sm shadow-xl shadow-amber-100" onClick={onClose}>
                  Acknowledged, Abort Action
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="danger" 
                  className="w-full py-5 text-sm shadow-xl shadow-rose-200" 
                  onClick={() => deleteMutation.mutate()} 
                  isLoading={deleteMutation.isPending}
                  icon={<Trash2 size={20} />}
                >
                  Confirm Registry Erasure
                </Button>
                <Button variant="ghost" onClick={onClose} className="w-full py-4 text-slate-400 hover:text-slate-900">
                  Cancel Action
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};